import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/cn";

interface InlineTextProps {
  value: string | null;
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
  ariaLabel: string;
  className?: string;
  emptyClassName?: string;
  onCommit: (next: string | null) => Promise<boolean>;
  saving?: boolean;
  error?: string | null;
}

export function InlineText({
  value,
  placeholder = "Нажми чтобы добавить",
  maxLength,
  multiline = false,
  ariaLabel,
  className,
  emptyClassName,
  onCommit,
  saving = false,
  error = null,
}: InlineTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [editing]);

  const startEdit = () => setEditing(true);

  const cancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  const commit = async () => {
    const trimmed = draft.trim();
    const next = trimmed.length === 0 ? null : trimmed;
    const ok = await onCommit(next);
    if (ok) setEditing(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
      return;
    }
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      void commit();
      return;
    }
    if (multiline && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void commit();
    }
  };

  if (!editing) {
    const isEmpty = value == null || value.length === 0;
    return (
      <button
        type="button"
        onClick={startEdit}
        aria-label={ariaLabel}
        className={cn(
          "block w-full min-h-touch text-left rounded-card px-2 py-1",
          "hover:bg-sbg/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          isEmpty ? emptyClassName ?? "text-hint italic" : className,
        )}
      >
        {isEmpty ? placeholder : multiline ? (
          <span className="whitespace-pre-wrap">{value}</span>
        ) : (
          value
        )}
      </button>
    );
  }

  const sharedProps = {
    ref: inputRef as never,
    value: draft,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDraft(e.target.value),
    onKeyDown: onKey,
    onBlur: () => void commit(),
    "aria-label": ariaLabel,
    disabled: saving,
    maxLength,
    className: cn(
      "block w-full min-h-touch rounded-card border border-hint/30 bg-sbg",
      "px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent",
      multiline ? "min-h-[6rem]" : "",
    ),
  };

  return (
    <div className="space-y-1">
      {multiline ? (
        <textarea {...(sharedProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} rows={4} />
      ) : (
        <input
          {...(sharedProps as React.InputHTMLAttributes<HTMLInputElement>)}
          type="text"
        />
      )}
      <div className="flex items-center justify-between text-xs text-hint">
        {error ? (
          <span className="text-dstr" role="alert">
            {error}
          </span>
        ) : (
          <span>
            {multiline ? "Ctrl/Cmd+Enter — сохранить, Esc — отмена" : "Enter — сохранить, Esc — отмена"}
          </span>
        )}
        {maxLength && (
          <span className="tabular-nums">
            {draft.length}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
}
